import React, { useState, useEffect } from 'react';
import { 
  Download, Search, AlertCircle, CheckCircle, XCircle, Phone, Mail, 
  Clock, Filter, MapPin, RefreshCw, X, Calendar
} from 'lucide-react';
import { useCompany } from '../../contexts/CompanyContext';
import { certificates } from '../../lib/api';
import { cn } from '../../utils/cn';
import { formatDate, formatRelativeTime } from '../../utils/date';
import { CertificateStats } from '../../components/CertificateStats';
import { parseISO, isValid, format, isAfter, subDays, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';

interface DriverCertification {
  id: string;
  driver: {
    id: string;
    name: string;
    cpf: string;
    email: string;
    phone: string;
    city: string;
  };
  score: number;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  moduleResults?: any[];
  completedAt: string;
  completed_at?: string;
}

type DateFilter = '24h' | '7d' | '30d' | '90d' | 'all';

const REJECTION_REASONS = [
  'Falta a CNH',
  'Falta o Comprovante de Endereço',
  'CNH Vencida',
  'Dados falsos',
  'Suspeita de uso de Terceiro',
  'Outros'
];

const DATE_FILTERS: { value: DateFilter; label: string }[] = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '3 meses' },
  { value: 'all', label: 'Todos' }
];

const REFRESH_INTERVAL = 30000;

export function CertificatesPage() {
  const { company } = useCompany();
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [evaluations, setEvaluations] = useState<DriverCertification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [selectedDrivers, setSelectedDrivers] = useState<string[]>([]);
  const [cities, setCities] = useState<{id: string, name: string}[]>([]);

  useEffect(() => {
    const storedCities = localStorage.getItem('cities');
    if (storedCities) {
      setCities(JSON.parse(storedCities));
    } else {
      const defaultCities = [
        { id: 'anapolisgo', name: 'Anápolis' },
        { id: 'rioverdego', name: 'Rio Verde' },
        { id: 'itumbiara', name: 'Itumbiara' }
      ];
      setCities(defaultCities);
      localStorage.setItem('cities', JSON.stringify(defaultCities));
    }
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    const intervalId = setInterval(() => {
      loadData();
    }, REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [autoRefresh]);

  const getCompletionDate = (evaluation: DriverCertification): Date | null => {
    const dateStr = evaluation.completedAt || evaluation.completed_at;
    if (!dateStr) return null;

    try {
      const date = parseISO(dateStr);
      return isValid(date) ? date : null;
    } catch (err) {
      console.warn('Invalid date:', dateStr);
      return null;
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const certificatesData = await certificates.list();
      
      const latestByStatus = certificatesData.reduce((acc: { [key: string]: { [status: string]: DriverCertification } }, curr: DriverCertification) => {
        if (!curr.driver?.cpf) return acc;

        const currDate = getCompletionDate(curr);
        if (!currDate) {
          console.warn('Invalid completion date:', curr);
          return acc;
        }

        const cpfKey = curr.driver.cpf;
        if (!acc[cpfKey]) {
          acc[cpfKey] = {};
        }

        const existingForStatus = acc[cpfKey][curr.status];
        if (!existingForStatus) {
          acc[cpfKey][curr.status] = curr;
        } else {
          const existingDate = getCompletionDate(existingForStatus);
          if (existingDate && currDate > existingDate) {
            acc[cpfKey][curr.status] = curr;
          }
        }

        return acc;
      }, {});

      const processedEvaluations = Object.values(latestByStatus)
        .flatMap(statusMap => Object.values(statusMap))
        .sort((a, b) => {
          const dateA = getCompletionDate(a) || new Date(0);
          const dateB = getCompletionDate(b) || new Date(0);
          return dateB.getTime() - dateA.getTime();
        });

      setEvaluations(processedEvaluations);
      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Erro ao carregar dados. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (driverId: string | string[], status: 'approved' | 'rejected', reason?: string) => {
    if (actionLoading) return;

    try {
      setActionLoading(Array.isArray(driverId) ? 'bulk' : driverId);
      setError(null);
      setSuccessMessage(null);

      const driverIds = Array.isArray(driverId) ? driverId : [driverId];
      
      const driverCertificates = evaluations.filter(e => driverIds.includes(e.driver.id));
      
      const updatePromises = driverCertificates.flatMap(cert => {
        const pendingCerts = evaluations.filter(e => 
          e.driver.cpf === cert.driver.cpf && 
          e.status === 'pending'
        );
        
        return pendingCerts.map(driverCert => 
          certificates.update(driverCert.id, status, reason)
        );
      });

      await Promise.all(updatePromises);

      await loadData();
      
      const message = `${driverCertificates.length} certificado(s) ${
        status === 'approved' ? 'aprovado(s)' : 'reprovado(s)'
      } com sucesso!`;

      toast.success(message, {
        duration: 4000,
        position: 'top-right',
        style: {
          background: status === 'approved' ? '#DEF7EC' : '#FDE8E8',
          color: status === 'approved' ? '#03543F' : '#9B1C1C',
          border: `1px solid ${status === 'approved' ? '#84E1BC' : '#F8B4B4'}`
        },
        icon: status === 'approved' ? '✅' : '❌'
      });
      
      setSelectedDrivers([]);
    } catch (err: any) {
      console.error('Error updating status:', err);
      toast.error(err.message || 'Erro ao atualizar status. Por favor, tente novamente.', {
        duration: 4000,
        position: 'top-right',
        style: {
          background: '#FEE2E2',
          color: '#991B1B',
          border: '1px solid #FCA5A5'
        }
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadCertificate = async (driverId: string) => {
    try {
      setDownloadingId(driverId);
      setError(null);
      const { signedUrl } = await certificates.generatePDF(driverId);
      window.open(signedUrl, '_blank');
    } catch (err: any) {
      setError(err.message || 'Erro ao gerar certificado. Por favor, tente novamente.');
    } finally {
      setDownloadingId(null);
    }
  };

  const stats = {
    total: evaluations.length,
    pending: evaluations.filter(e => e.status === 'pending').length,
    approved: evaluations.filter(e => e.status === 'approved').length,
    rejected: evaluations.filter(e => e.status === 'rejected').length,
    archived: evaluations.filter(e => e.status === 'archived').length,
    averageScore: evaluations.length 
      ? evaluations.reduce((acc, curr) => acc + curr.score, 0) / evaluations.length 
      : 0
  };

  const getDateFilterCutoff = (filter: DateFilter): Date | null => {
    const now = new Date();
    switch (filter) {
      case '24h':
        return subDays(now, 1);
      case '7d':
        return subDays(now, 7);
      case '30d':
        return subDays(now, 30);
      case '90d':
        return subMonths(now, 3);
      default:
        return null;
    }
  };

  const filteredEvaluations = evaluations.filter(evaluation => {
    const matchesSearch = 
      evaluation.driver.name.toLowerCase().includes(search.toLowerCase()) ||
      evaluation.driver.cpf.includes(search) ||
      evaluation.driver.email.toLowerCase().includes(search.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || evaluation.status === statusFilter;
    const matchesCity = !cityFilter || evaluation.driver.city === cityFilter;
    
    const cutoffDate = getDateFilterCutoff(dateFilter);
    const evaluationDate = getCompletionDate(evaluation);
    const matchesDate = !cutoffDate || !evaluationDate || isAfter(evaluationDate, cutoffDate);
    
    return matchesSearch && matchesStatus && matchesCity && matchesDate;
  });

  const handleSelectDriver = (driverId: string) => {
    setSelectedDrivers(prev => 
      prev.includes(driverId)
        ? prev.filter(id => id !== driverId)
        : [...prev, driverId]
    );
  };

  const handleSelectAll = () => {
    setSelectedDrivers(prev => 
      prev.length === filteredEvaluations.length
        ? []
        : filteredEvaluations.map(e => e.driver.id)
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Certificações</h1>
          <p className="mt-1 text-gray-500">
            Gerencie as avaliações dos motoristas
            <span className="ml-2 text-xs text-gray-400">
              Última atualização: {formatRelativeTime(lastRefresh)}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={cn(
              "flex items-center gap-2 rounded-lg px-4 py-2 text-sm",
              "transition-colors border",
              autoRefresh 
                ? "border-purple-200 bg-purple-50 text-purple-700"
                : "border-gray-200 bg-white text-gray-700"
            )}
          >
            <RefreshCw className={cn(
              "h-4 w-4",
              autoRefresh && "animate-spin"
            )} />
            {autoRefresh ? 'Atualização Automática' : 'Atualização Manual'}
          </button>
          <button
            onClick={loadData}
            disabled={loading}
            className={cn(
              "p-2 rounded-lg text-gray-600 hover:text-gray-900",
              "transition-colors hover:bg-gray-100",
              "disabled:opacity-50"
            )}
          >
            <RefreshCw className={cn(
              "h-5 w-5",
              loading && "animate-spin"
            )} />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="rounded-lg bg-green-50 p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      <CertificateStats stats={stats} />

      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="relative flex-1 max-w-xs w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, CPF, email..."
              className={cn(
                "block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg",
                "text-gray-900 placeholder-gray-500 focus:ring-purple-500 focus:border-purple-500"
              )}
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className={cn(
                "block w-full sm:w-auto rounded-lg border-gray-300",
                "focus:ring-purple-500 focus:border-purple-500"
              )}
            >
              <option value="all">Todos os Status</option>
              <option value="pending">Pendentes ({stats.pending})</option>
              <option value="approved">Aprovados ({stats.approved})</option>
              <option value="rejected">Reprovados ({stats.rejected})</option>
            </select>

            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className={cn(
                "block w-full sm:w-auto rounded-lg border-gray-300",
                "focus:ring-purple-500 focus:border-purple-500"
              )}
            >
              <option value="">Todas as Cidades</option>
              {cities.map(city => (
                <option key={city.id} value={city.name}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-gray-400" />
          {DATE_FILTERS.map(filter => (
            <button
              key={filter.value}
              onClick={() => setDateFilter(filter.value)}
              className={cn(
                "px-3 py-1 text-sm rounded-lg transition-colors",
                dateFilter === filter.value
                  ? "bg-purple-100 text-purple-700 font-medium"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100"
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {selectedDrivers.length > 0 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {selectedDrivers.length} motorista(s) selecionado(s)
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleUpdateStatus(selectedDrivers, 'approved')}
                disabled={actionLoading === 'bulk'}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg",
                  "text-white bg-green-600 hover:bg-green-700",
                  "transition-colors disabled:opacity-50"
                )}
              >
                <CheckCircle className="h-4 w-4" />
                Aprovar Selecionados
              </button>
              <button
                onClick={() => handleUpdateStatus(selectedDrivers, 'rejected')}
                disabled={actionLoading === 'bulk'}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg",
                  "text-white bg-red-600 hover:bg-red-700",
                  "transition-colors disabled:opacity-50"
                )}
              >
                <XCircle className="h-4 w-4" />
                Rejeitar Selecionados
              </button>
            </div>
          </div>
        )}

        <div className="mt-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedDrivers.length === filteredEvaluations.length}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Motorista
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nota
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEvaluations.map((evaluation) => (
                  <tr key={evaluation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedDrivers.includes(evaluation.driver.id)}
                        onChange={() => handleSelectDriver(evaluation.driver.id)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {evaluation.driver.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {evaluation.driver.cpf}
                        </div>
                        <div className="flex items-center gap-4 mt-1">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Mail className="h-3 w-3" />
                            {evaluation.driver.email}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Phone className="h-3 w-3" />
                            {evaluation.driver.phone}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <MapPin className="h-3 w-3" />
                            {evaluation.driver.city}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {evaluation.status === 'pending' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Clock className="h-3 w-3" />
                          Pendente
                        </span>
                      )}
                      {evaluation.status === 'approved' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3" />
                          Aprovado
                        </span>
                      )}
                      {evaluation.status === 'rejected' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircle className="h-3 w-3" />
                          Reprovado
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {evaluation.score.toFixed(1)}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(evaluation.completedAt || evaluation.completed_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {evaluation.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleUpdateStatus(evaluation.driver.id, 'approved')}
                              disabled={actionLoading === evaluation.driver.id}
                              className={cn(
                                "text-green-600 hover:text-green-900",
                                "transition-colors p-1 rounded-lg hover:bg-green-50"
                              )}
                              title="Aprovar Todos os Certificados"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(evaluation.driver.id, 'rejected')}
                              disabled={actionLoading === evaluation.driver.id}
                              className={cn(
                                "text-red-600 hover:text-red-900",
                                "transition-colors p-1 rounded-lg hover:bg-red-50"
                              )}
                              title="Rejeitar Todos os Certificados"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {evaluation.status === 'approved' && (
                          <button
                            onClick={() => handleDownloadCertificate(evaluation.driver.id)}
                            disabled={downloadingId === evaluation.driver.id}
                            className={cn(
                              "text-blue-600 hover:text-blue-900",
                              "transition-colors p-1 rounded-lg hover:bg-blue-50"
                            )}
                            title="Baixar Certificado"
                          >
                            {downloadingId === evaluation.driver.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}